"use server";
/**
 * Current subscription plan + usage, for the plan banner. Reads the tier from
 * accounts (via entitlement.resolvePlan, keyed by the session identity) and joins
 * it with this account's habit counts so the UI can show "used vs allowed" and
 * flag anyone already over the tier cap (legacy habits created before the cap).
 */
import { SessionService } from "@/contexts/authentication";
import { resolvePlan, PLAN_LABELS, type PlanTier, type TierLimits } from "../services/entitlement";
import { getRoster } from "./people";
import { listHabits } from "./habits";

export interface PlanUsage {
  tier: PlanTier;
  label: string;
  isPaid: boolean;
  isTop: boolean;
  limits: TierLimits;
  totalHabits: number;
  perPerson: { personId: string; name: string; count: number; overLimit: boolean }[];
}

export async function getMyPlan(): Promise<PlanUsage | null> {
  const user = await SessionService.getUser();
  if (!user?.accountId) return null;

  const [{ tier, limits }, roster, habits] = await Promise.all([
    resolvePlan(user.accountId, user.identityId ?? undefined),
    getRoster(),
    listHabits(),
  ]);

  const counts = new Map<string, number>();
  for (const h of habits) counts.set(h.personId, (counts.get(h.personId) ?? 0) + 1);

  const perPerson = roster.map((p) => {
    const count = counts.get(p.id) ?? 0;
    return { personId: p.id, name: p.name, count, overLimit: count > limits.habitsPerPerson };
  });

  return {
    tier,
    label: PLAN_LABELS[tier],
    isPaid: tier !== "free",
    isTop: tier === "family",
    limits,
    totalHabits: habits.length,
    perPerson,
  };
}

/** Read + clear the one-shot downgrade notice (the UI toasts it once). */
export async function takeDowngradeNotice() {
  const accountId = await SessionService.getAccountId();
  if (!accountId) return null;
  const { getPlanTransitionService } = await import("../services/PlanTransitionService");
  return getPlanTransitionService().takePendingNotice(accountId);
}
